import React, { useMemo, useRef, useEffect } from 'react';
import { Alert, Button } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import {
    validateLiveStreamUrl,
    getPlatformInfo,
    SUPPORTED_LIVE_STREAM_PLATFORMS,
    buildTikTokLiveEmbedUrl,
    getTikTokEmbedDomain
} from '../../utils/videoUtils';

const stopIframePlayback = (iframe) => {
    if (!iframe) return;

    try {
        iframe.contentWindow?.postMessage({ type: 'pause', 'x-tiktok-player': true }, '*');
    } catch (error) {
        // Ignore cross-origin messaging errors
    }

    iframe.src = 'about:blank';
};

const LiveStreamEmbed = ({
    url,
    title = 'Live Stream',
    style = {},
    compact = false,
    maxHeight,
    active = true
}) => {
    const iframeRef = useRef(null);
    const streamInfo = useMemo(() => validateLiveStreamUrl(url), [url]);

    const iframeSrc = useMemo(() => {
        if (!streamInfo.isValid) return null;

        if (streamInfo.embedType === 'tiktok-live') {
            return buildTikTokLiveEmbedUrl(streamInfo.videoId);
        }

        return streamInfo.embedUrl;
    }, [streamInfo]);

    const embedDomain = useMemo(() => getTikTokEmbedDomain(), []);

    useEffect(() => {
        if (!active) {
            stopIframePlayback(iframeRef.current);
        }
    }, [active]);

    useEffect(() => {
        return () => {
            stopIframePlayback(iframeRef.current);
        };
    }, []);

    if (!url || !active) {
        return null;
    }

    if (!streamInfo.isValid || !iframeSrc) {
        return (
            <Alert
                type="warning"
                message="Invalid live stream URL"
                description="Please enter a valid YouTube, TikTok, or Facebook live stream URL."
                showIcon
            />
        );
    }

    const platformInfo = SUPPORTED_LIVE_STREAM_PLATFORMS[streamInfo.platform] || getPlatformInfo(streamInfo.platform);
    const isTikTokLive = streamInfo.embedType === 'tiktok-live';
    const isTikTokPlayer = streamInfo.platform === 'tiktok' && streamInfo.embedType === 'iframe';
    const embedHeight = maxHeight || (compact ? 130 : 200);

    const playerHeight = isTikTokLive
        ? Math.max(embedHeight, compact ? 220 : 280)
        : isTikTokPlayer
            ? Math.max(embedHeight, compact ? 200 : 280)
            : embedHeight;

    return (
        <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', ...style }}>
            {!compact && (
                <div style={{
                    marginBottom: 6,
                    fontSize: 12,
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                }}>
                    <span>{platformInfo?.icon || '📺'}</span>
                    <span>{platformInfo?.name || 'Live Stream'}</span>
                </div>
            )}
            <div style={{
                position: 'relative',
                width: '100%',
                minWidth: isTikTokLive || isTikTokPlayer ? 325 : undefined,
                height: playerHeight,
                maxHeight: playerHeight,
                borderRadius: 8,
                overflow: 'hidden',
                background: '#000'
            }}>
                <iframe
                    ref={iframeRef}
                    key={iframeSrc}
                    src={iframeSrc}
                    title={title}
                    scrolling="no"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        borderRadius: 8,
                        overflow: 'hidden'
                    }}
                    allow={isTikTokLive ? 'autoplay; fullscreen' : 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen'}
                    allowFullScreen
                />
            </div>
            {isTikTokLive && (
                <div style={{ marginTop: 8 }}>
                    <Alert
                        type="info"
                        showIcon
                        message="TikTok Live embed"
                        description={`Playback requires ${embedDomain} to be approved by TikTok for LIVE embeds. If the stream does not load, open it directly in TikTok.`}
                        style={{ marginBottom: 8, fontSize: 12 }}
                    />
                    <div style={{ textAlign: 'center' }}>
                        <Button
                            type="link"
                            size="small"
                            icon={<ExportOutlined />}
                            href={streamInfo.originalUrl || url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Open in TikTok
                        </Button>
                    </div>
                </div>
            )}
            {isTikTokPlayer && (
                <div style={{ marginTop: 8, textAlign: 'center' }}>
                    <Button
                        type="link"
                        size="small"
                        icon={<ExportOutlined />}
                        href={streamInfo.originalUrl || url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Open in TikTok if video does not load
                    </Button>
                </div>
            )}
        </div>
    );
};

export default LiveStreamEmbed;
